<template>
	<panel :show="show" large @close="close">
		<template #title>
			Switch Partner
		</template>
		<template>
			<img
				src="@/assets/img/undraw-switches.svg"
				class="panel-illustration"
			/>
			<form class="panel-form" @submit.prevent="onFormSubmit">
				<div class="form-group">
					<label for="providerType" class="form-label">
						Active Partner
					</label>
					<multiselect
						id="provider_id"
						ref="multiselect"
						v-model="partner"
						required
						track-by="id"
						label="label"
						placeholder="Select a partner"
						deselect-label=""
						:options-limit="9999"
						:options="allPartners"
						:multiple="false"
						:allow-empty="false"
						:select-label="''"
						:selected-label="''"
						@open="onDropdownOpen"
						@search-change="onKeywordChange"
						@select="onPartnerSelect"
						@close="onBlur"
					>
						<template slot="noResult">
							<div v-if="loading" style="text-align: center;">
								<span>Please wait...</span>
								<font-awesome-icon
									class="ml-2"
									:icon="['far', 'circle-notch']"
									spin
								/>
							</div>
							<div v-else>
								No partners found
							</div>
						</template>
						<template slot="singleLabel" slot-scope="{ option }">
							<div class="multiselect-item-content">
								{{ option.label }}
								<span
									v-if="shouldDisplayCallCount"
									class="partner-select-count-badge"
								>
									{{ option.pending_calls_count }}
								</span>
							</div>
						</template>
						<template slot="option" slot-scope="{ option }">
							<div class="multiselect-item-content">
								<span>
									{{ option.label }}
								</span>
								<span
									v-if="shouldDisplayCallCount"
									class="partner-select-count-badge"
								>
									{{ option.pending_calls_count }}
								</span>
							</div>
						</template>

						<!-- Sentinel div for infinite scroll -->
						<template #afterList>
							<div
								ref="sentinel"
								style="height: 8px; width: 100%;"
							></div>
							<div
								v-if="scrollLoading"
								style="text-align: center;"
							>
								<span>Please wait...</span>
								<font-awesome-icon
									class="ml-2"
									:icon="['far', 'circle-notch']"
									spin
								/>
							</div>
						</template>
					</multiselect>
				</div>
				<div class="flex justify-between">
					<button
						type="button"
						class="btn btn-danger w-50 mr-2"
						@click.prevent="close"
					>
						<font-awesome-icon
							class="btn-icon"
							:icon="['far', 'times-octagon']"
						/>
						<span class="btn-label">Cancel</span>
					</button>
					<button
						ref="last"
						type="submit"
						class="btn btn-outline-success w-50 ml-2"
						@keydown.tab="onLastElementTab"
					>
						<font-awesome-icon
							class="btn-icon"
							:icon="['far', 'save']"
							aria-hidden="true"
						/>
						<span class="btn-label">Change</span>
					</button>
				</div>
			</form>
		</template>
	</panel>
</template>
<script>
import Panel from './Panel'
import util from '@/util'
import Multiselect from 'vue-multiselect'
import PanelMixin from '@/mixins/PanelMixin'
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'
import 'vue-multiselect/dist/vue-multiselect.min.css'
import { debounce } from 'lodash'
import { UPDATE_PAGINATION_CONFIG } from '@/store/mutation-types'

const MIN_SEARCH_LENGTH = 3
const DEBOUNCE_DELAY = 500
const SCROLL_ROOT_MARGIN = '50px'

export default {
	components: {
		Multiselect,
		Panel,
	},
	computed: {
		allPartners() {
			if (!this.partners) {
				return []
			}

			const sortedPartners = util.sortPartners(this.partners || [])

			return sortedPartners.map(partner => ({
				id: partner.id,
				label: partner.name,
				pending_calls_count: partner?.pending_calls_count || 0,
			}))
		},

		shouldDisplayCallCount() {
			return !this.hasRole('admin')
		},

		...mapGetters({
			hasRole: 'auth/hasRole',
			partners: 'partners',
			active: 'partner',
		}),

		...mapState('partners', {
			currentPage: state => state?.pagination?.current_page,
			lastPage: state => state?.pagination?.last_page,
		}),
	},
	methods: {
		// Form handling
		async onFormSubmit() {
			try {
				await this.setActive(this.partner.id)
				if (this.$route.name === 'app.providers.edit') {
					this.$router.push({ name: 'app.providers' })
				}
				this.updatePaginationConfig({ searchKeyword: '' })
			} catch (error) {
				this.$alert.error('partners.change.error')
			} finally {
				this.close()
			}
		},

		// Partner selection handlers
		async onPartnerSelect(selectedPartner) {
			this.ignoreNextSearch = true
			await this.resetPagination()
			await this.getPartnerList({ selectedPartner })
			this.partner = selectedPartner
		},

		// Search handlers
		async onKeywordChange(keyword) {
			this.scrollLoading = false
			this.loading = true

			if (!keyword && !this.ignoreNextSearch) {
				await this.resetPagination()
				await this.getPartnerList()
				return (this.loading = false)
			}

			this.onSearchKeywordChange(keyword)
		},

		async onSearchKeywordChange(keyword) {
			this.ignoreNextSearch = false

			if (keyword.length < MIN_SEARCH_LENGTH) {
				return (this.loading = false)
			}

			try {
				this.loading = true
				await this.updatePaginationConfig({
					current_page: 1,
					searchKeyword: keyword,
				})
				await this.getPartnerList({ search: true })
			} catch (error) {
				this.$alert.error('Error for searching partners.')
			} finally {
				this.loading = false
			}
		},

		// Dropdown handlers
		async onDropdownOpen() {
			this.$nextTick(() => {
				this.cleanupObserver()
				this.setupInfiniteScroll()
			})
		},

		onBlur() {
			if (!this.ignoreNextSearch) {
				this.resetPagination()
				this.getPartnerList()
			}
		},

		// Infinite scroll handlers
		async fetchPartners() {
			if (this.currentPage === this.lastPage) return
			if (this.scrollLoading || !this.lastPage) return

			try {
				this.scrollLoading = true
				await this.updatePaginationConfig({
					current_page: this.currentPage + 1,
				})
				await this.getPartnerList({ search: false })
			} catch (error) {
				this.$alert.error('Error for searching partners.')
			} finally {
				this.scrollLoading = false
			}
		},

		setupInfiniteScroll() {
			const sentinel = this.$refs.sentinel
			const multiselectEl =
				this.$refs.multiselect && this.$refs.multiselect.$el
			const dropdown = multiselectEl?.querySelector(
				'.multiselect__content-wrapper'
			)

			if (!sentinel || !dropdown) return

			this.observer = new IntersectionObserver(
				([entry]) => {
					if (entry.isIntersecting && !this.loading) {
						this.fetchPartners()
					}
				},
				{
					root: dropdown,
					rootMargin: SCROLL_ROOT_MARGIN,
					threshold: 0,
				}
			)

			this.observer.observe(sentinel)
		},

		cleanupObserver() {
			if (this.observer) {
				this.observer.disconnect()
				this.observer = null
			}
		},

		// Helper methods
		mapPartnerToOption(partner) {
			return {
				id: partner.id,
				label: partner.name,
				pending_calls_count: partner.pending_calls_count,
			}
		},

		resetPagination() {
			this.updatePaginationConfig({
				searchKeyword: '',
				current_page: 1,
			})
		},

		// Vuex mappings
		...mapActions('partners', ['setActive', 'getPartnerList']),

		...mapMutations('partners', {
			updatePaginationConfig: UPDATE_PAGINATION_CONFIG,
		}),
	},
	mixins: [PanelMixin],
	name: 'SwitchPartnerPanel',
	props: {},
	watch: {
		active: {
			handler(partner) {
				if (partner) {
					this.partner = this.mapPartnerToOption(partner)
				}
			},
			immediate: true,
		},
		show: {
			handler(isVisible) {
				if (isVisible) {
					this.resetPagination()
					this.getPartnerList()
				}
			},
		},
	},
	created() {
		this.onSearchKeywordChange = debounce(
			this.onSearchKeywordChange.bind(this),
			DEBOUNCE_DELAY
		)
	},
	beforeUnmount() {
		this.cleanupObserver()
		this.onSearchKeywordChange.cancel()
	},
	data() {
		return {
			partner: {},
			observer: null,
			loading: false,
			scrollLoading: false,
			ignoreNextSearch: false,
		}
	},
}
</script>
